import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Ticket, Tag, Users, TrendingUp, Calendar, Percent, CheckCircle, XCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { getAuthHeader } = useAdminAuth();
  const [stats, setStats] = useState(null);
  const [discountStats, setDiscountStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    fetchDiscountStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscountStats = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/discounts/stats`, {
        headers: getAuthHeader()
      });
      setDiscountStats(response.data);
    } catch (error) {
      console.error('Failed to fetch discount stats:', error);
    }
  };

  const statCards = [
    {
      title: 'Sprzedane karnety',
      value: stats?.passes?.total || 0,
      subtext: `${stats?.passes?.today || 0} dzisiaj`,
      icon: Ticket,
      color: 'purple'
    },
    {
      title: 'Rezerwacje online',
      value: stats?.reservations?.total || 0,
      subtext: `${stats?.reservations?.today || 0} dzisiaj`,
      icon: Calendar,
      color: 'green'
    },
    {
      title: 'Aktywne rabaty',
      value: stats?.discounts?.active || 0,
      subtext: 'Kody promocyjne',
      icon: Tag,
      color: 'orange'
    },
    {
      title: 'Użytkownicy',
      value: stats?.users?.total || 0,
      subtext: 'Zarejestrowani',
      icon: Users,
      color: 'blue'
    }
  ];

  const colorClasses = {
    purple: 'bg-purple-500/10 text-purple-400',
    green: 'bg-green-500/10 text-green-400',
    orange: 'bg-orange-500/10 text-orange-400',
    blue: 'bg-blue-500/10 text-blue-400'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400">Przegląd statystyk AvePark</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="bg-white/5 border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-400 text-sm">{stat.title}</p>
                  <p className="text-2xl font-bold text-white mt-1">{stat.value}</p>
                  <p className="text-gray-500 text-xs mt-1">{stat.subtext}</p>
                </div>
                <div className={`p-3 rounded-lg ${colorClasses[stat.color]}`}>
                  <stat.icon className="w-6 h-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Monthly Overview */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-400" />
            Podsumowanie miesiąca
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <p className="text-3xl font-bold text-purple-400">{stats?.passes?.thisMonth || 0}</p>
              <p className="text-gray-400 text-sm mt-1">Karnetów w tym miesiącu</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <p className="text-3xl font-bold text-green-400">
                {Math.round((stats?.passes?.thisMonth || 0) / 30 * 10) / 10}
              </p>
              <p className="text-gray-400 text-sm mt-1">Średnio dziennie</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <div className="flex items-center justify-center gap-2">
                <TrendingUp className="w-6 h-6 text-green-400" />
                <p className="text-3xl font-bold text-green-400">+12%</p>
              </div>
              <p className="text-gray-400 text-sm mt-1">Wzrost m/m</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Discount Codes Statistics */}
      <Card className="bg-white/5 border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Tag className="w-5 h-5 text-orange-400" />
            Statystyki kodów rabatowych
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <p className="text-3xl font-bold text-orange-400">{stats?.discounts?.active || 0}</p>
              <p className="text-gray-400 text-sm mt-1">Aktywnych kodów</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <p className="text-3xl font-bold text-green-400">
                {discountStats?.perCode?.reduce((sum, s) => sum + (s.totalUsed || 0), 0) || 0}
              </p>
              <p className="text-gray-400 text-sm mt-1">Łącznie użyć</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <p className="text-3xl font-bold text-blue-400">
                {(discountStats?.perCode?.reduce((sum, s) => sum + (s.totalDiscount || 0), 0) || 0).toFixed(0)} zł
              </p>
              <p className="text-gray-400 text-sm mt-1">Suma rabatów</p>
            </div>
            <div className="text-center p-4 bg-white/5 rounded-lg">
              <p className="text-3xl font-bold text-purple-400">
                {discountStats?.perCode?.filter(s => s.totalUsed > 0).length || 0}
              </p>
              <p className="text-gray-400 text-sm mt-1">Kodów wykorzystanych</p>
            </div>
          </div>
          
          {/* Top used codes */}
          {discountStats?.perCode && discountStats.perCode.filter(s => s.totalUsed > 0).length > 0 && (
            <div>
              <h4 className="text-sm text-gray-400 mb-3">Najczęściej używane kody:</h4>
              <div className="space-y-2">
                {discountStats.perCode
                  .filter(s => s.totalUsed > 0 && s.code)
                  .sort((a, b) => b.totalUsed - a.totalUsed)
                  .slice(0, 5)
                  .map((code, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-orange-400 font-mono font-medium">{code.code || code.discountCode}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-green-400">{code.totalUsed}x użyty</span>
                        <span className="text-blue-400">{(code.totalDiscount || 0).toFixed(0)} zł</span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}
          
          {(!discountStats?.perCode || discountStats.perCode.filter(s => s.totalUsed > 0).length === 0) && (
            <p className="text-gray-500 text-center py-4">Brak danych o wykorzystaniu kodów</p>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="bg-white/5 border-white/10 mt-2">
        <CardHeader>
          <CardTitle className="text-white">Szybkie akcje</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <a href="/admin/hero" className="p-4 bg-purple-500/10 hover:bg-purple-500/20 rounded-lg text-center transition-colors">
              <p className="text-purple-400 font-medium">Edytuj Hero</p>
            </a>
            <a href="/admin/news" className="p-4 bg-blue-500/10 hover:bg-blue-500/20 rounded-lg text-center transition-colors">
              <p className="text-blue-400 font-medium">Dodaj aktualność</p>
            </a>
            <a href="/admin/discounts" className="p-4 bg-orange-500/10 hover:bg-orange-500/20 rounded-lg text-center transition-colors">
              <p className="text-orange-400 font-medium">Nowy kod rabatowy</p>
            </a>
            <a href="/admin/passes" className="p-4 bg-green-500/10 hover:bg-green-500/20 rounded-lg text-center transition-colors">
              <p className="text-green-400 font-medium">Przeglądaj karnety</p>
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;
